import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, AfterViewInit, ViewChildren, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { PaygService } from '../../../services/payg.service';
import { PaygCumulativeCreditStatusModel } from '../../../models/payg/payg-cumulative-credit-status.model';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';

@Component({
    selector: 'cumulative-credit-status-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; display:block; }'],
    host: { 'class': 'block-detail' }
})
export class CumulativeCreditStatusChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    public cumulativeCreditStatus: PaygCumulativeCreditStatusModel[] = [];
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    gradientStroke: any;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number;

    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(
        private paygService: PaygService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) { }



    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId');
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {
        this.paygService.getCumulativeCreditStatus(this.clientId, this.siteId, this.timePeriod.toString()).then().then((result) => {
            this.cumulativeCreditStatus = result;
            this.chartData = this.processData(this.cumulativeCreditStatus);
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            if (this.chartData.labels.length > 0) {
                this.createChart();
            }
        }).catch((reason) => {
            this.hasDataChange.emit(false);
            this.hasLoadingChange.emit(false);
        });
    }

    processData(data: PaygCumulativeCreditStatusModel[]) {

        const dateOrderedDate = data.sort((a, b) => a.date > b.date ? 1 : -1);
        let graphData = {
            labels: [],
            negative: [],
            positive: []
        };

        for (var i = 0; i < dateOrderedDate.length; i++) {

            if (dateOrderedDate && dateOrderedDate.length > 0) graphData.labels.push(this.pipe.transform(dateOrderedDate[i].date, 'MM/yy'));
            graphData.negative.push(dateOrderedDate[i].negativeCreditStatus);
            graphData.positive.push(dateOrderedDate[i].positiveCreditStatus);
        }

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'horizontalBar',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            return hoverData.label + ' £' + tooltipData;
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Value (£)',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        gridLines: {
                            display: true
                        }
                    }],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Month',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            barThickness: 8,
                            id: "y-axis-0",
                            position: 'left',
                            gridLines: {
                                display: false
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;
        if (!this.hasData && this.chart) this.chart.destroy();
        let datasets = [] as any;

        datasets.push({
            label: 'Negative',
            yAxisID: 'y-axis-0',
            data: this.chartData.negative,
            backgroundColor: '#00539f'
        });

        datasets.push({
            label: 'Positive',
            yAxisID: 'y-axis-0',
            data: this.chartData.positive,
            backgroundColor: '#009aa3'
        });

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
